import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { ApiResponse } from 'src/app/core/models/api-response';
import { IdName } from 'src/app/shared/models/id-name';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import {
  ILinkSyncResponse,
  IProject,
  IProjectExportProcess,
  IProjectSyncResponse,
} from '../models/project.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectApiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private get baseUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/projects`;
  }

  constructor(private http: HttpClient) {}

  getProjects(filter?: any): Observable<ApiResponse<IProject[]>> {
    let params = createFilterParams(filter);

    if (filter?.searchTerm) {
      params = params.set('GlobalSearch', filter.searchTerm);
    }

    if (filter?.additional?.includeCompleted) {
      params = params.set('includeCompleted', 'true');
    }

    return this.http.get<ApiResponse<IProject[]>>(this.baseUrl, { params });
  }

  createProject(project: Omit<IdName, 'id'>): Observable<IdName> {
    return this.http.post<IdName>(this.baseUrl, project, this.httpOptions);
  }

  getProjectById(id: string): Observable<IProject> {
    return this.http.get<IProject>(`${this.baseUrl}/${id}`);
  }

  updateProject(project: IdName): Observable<IdName> {
    return this.http.put<IdName>(`${this.baseUrl}/${project.id}`, project, this.httpOptions);
  }

  deleteProject(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  updateProjectStatus(projectId: string, status: any): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${projectId}/status`, status, this.httpOptions);
  }

  assignMaintenanceJobsToProject(projectId: string, maintenanceJobIds: string[]): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/${projectId}/MaintenanceJobs/Assign`,
      { maintenanceJobIds },
      this.httpOptions
    );
  }

  removeMaintenanceJobsFromProject(
    projectId: string,
    maintenanceJobIds: string[]
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/${projectId}/MaintenanceJobs/Remove`,
      { id: projectId, maintenanceJobIds },
      this.httpOptions
    );
  }

  syncWithMarineManager(projectId: string): Observable<IProjectSyncResponse> {
    return this.http.post<IProjectSyncResponse>(
      `${this.baseUrl}/${projectId}/sync/marine-manager`,
      {},
      this.httpOptions
    );
  }

  syncLinkWithMarineManager(projectId: string, linkId: string): Observable<ILinkSyncResponse> {
    return this.http.post<ILinkSyncResponse>(
      `${this.baseUrl}/${projectId}/sync/marine-manager/${linkId}`,
      {},
      this.httpOptions
    );
  }

  exportToMarineManager(projectId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/${projectId}/export/marine-manager`,
      {},
      this.httpOptions
    );
  }

  completeProject(id: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/complete`, { id }, this.httpOptions);
  }

  getExportErrors(projectId: string): Observable<IProjectExportProcess[]> {
    return this.http.get<IProjectExportProcess[]>(
      `${this.baseUrl}/${projectId}/export/marine-manager/errors`
    );
  }
}
